/**
 * ResetPassword.tsx — Password reset completion page.
 *
 * Features:
 *  - Reads ?token= from URL query string
 *  - Live password strength meter (0-100 score, color bar)
 *  - Live requirements checklist
 *  - Confirm password match validation
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SecurityIcon from '@mui/icons-material/Security';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import axios from 'axios';
import API_BASE from '../../config/api';

// ── Password strength ──────────────────────────────────────────────────────────

interface Strength {
  score: number;
  hasLength: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

function evaluateStrength(password: string): Strength {
  const hasLength    = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber    = /[0-9]/.test(password);
  const hasSpecial   = /[^A-Za-z0-9]/.test(password);

  let score = 0;
  if (hasLength) {
    score += 20;
    score += Math.min(36, (password.length - 8) * 3);
  }
  if (hasUppercase) score += 15;
  if (hasNumber)    score += 15;
  if (hasSpecial)   score += 14;

  return { score, hasLength, hasUppercase, hasNumber, hasSpecial };
}

function strengthColor(score: number): string {
  if (score < 30) return '#ef233c';
  if (score < 55) return '#f77f00';
  if (score < 75) return '#f4d03f';
  return '#2ecc71';
}

function strengthLabel(score: number): string {
  if (score < 30) return 'Weak';
  if (score < 55) return 'Fair';
  if (score < 75) return 'Good';
  return 'Strong';
}

// ── Requirement row ────────────────────────────────────────────────────────────

function Req({ met, label }: { met: boolean; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      {met
        ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
        : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
      <Typography variant="caption" color={met ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPwd, setShowPwd]     = useState(false);
  const [showCfm, setShowCfm]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);

  const strength = useMemo(() => evaluateStrength(password), [password]);
  const isValid  = strength.hasLength && strength.hasUppercase && strength.hasNumber && strength.hasSpecial;
  const matches  = confirm.length > 0 && password === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !matches) return;
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Alert severity="error">Invalid reset link. Please request a new one.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d1117 0%, #0a1929 100%)',
        px: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 460,
          borderRadius: 3,
          border: '1px solid rgba(0,180,216,0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
          <SecurityIcon sx={{ fontSize: 36, color: '#00E5FF', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
        </Box>
        <Typography variant="h6" textAlign="center" mt={2} mb={3}>
          Set a new password
        </Typography>

        {success ? (
          <Alert severity="success">
            Password updated! Redirecting to login…
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* New password */}
            <TextField
              label="New password"
              type={showPwd ? 'text' : 'password'}
              fullWidth
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd((p) => !p)} edge="end" size="small">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            {/* Strength bar */}
            {password.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Password strength</Typography>
                  <Typography variant="caption" sx={{ color: strengthColor(strength.score), fontWeight: 700 }}>
                    {strengthLabel(strength.score)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={strength.score}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: strengthColor(strength.score),
                      borderRadius: 3,
                    },
                  }}
                />
                <Box mt={1}>
                  <Req met={strength.hasLength}    label="At least 8 characters" />
                  <Req met={strength.hasUppercase} label="One uppercase letter" />
                  <Req met={strength.hasNumber}    label="One number" />
                  <Req met={strength.hasSpecial}   label="One special character (!@#$…)" />
                </Box>
              </Box>
            )}

            {/* Confirm password */}
            <TextField
              label="Confirm new password"
              type={showCfm ? 'text' : 'password'}
              fullWidth
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={confirm.length > 0 && !matches}
              helperText={confirm.length > 0 && !matches ? "Passwords don't match" : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCfm((p) => !p)} edge="end" size="small">
                      {showCfm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !isValid || !matches}
              sx={{ fontWeight: 700, py: 1.5, mb: 2 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
            </Button>

            <Box textAlign="center">
              <Link component={RouterLink} to="/login" variant="body2" color="primary">
                Back to Sign In
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
